import { createContext, useContext, useCallback, useMemo, useState, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { Cart, ProductCard, NotificationPreferences } from '@alkor/types';
import { isPriceOnRequest } from '@alkor/utils';
import { api, ApiClientError } from '@/lib/api';
import { haptic } from '@/lib/telegram';
import { track } from '@/lib/analytics';

interface ShopState {
  cart: Cart | undefined;
  cartLoading: boolean;
  cartCount: number;
  cartTotal: number;
  inCart: (productId: string) => boolean;
  addToCart: (product: ProductCard) => Promise<void>;
  setQuantity: (itemId: string, quantity: number) => Promise<void>;
  removeItem: (itemId: string) => Promise<void>;
  clearCart: () => Promise<void>;
  busy: boolean;

  favorites: ProductCard[];
  isFavorite: (productId: string) => boolean;
  toggleFavorite: (product: ProductCard) => Promise<void>;

  subscribedIds: string[];
  isSubscribed: (productId: string) => boolean;
  subscribeStock: (product: ProductCard) => Promise<void>;

  toast: string | null;
  showToast: (message: string) => void;
}

const Ctx = createContext<ShopState | null>(null);

export function ShopProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [toast, setToast] = useState<string | null>(null);

  const showToast = useCallback((message: string) => {
    setToast(message);
    window.setTimeout(() => setToast((t) => (t === message ? null : t)), 2600);
  }, []);

  const cartQuery = useQuery({ queryKey: ['cart'], queryFn: api.cart, staleTime: 10_000 });
  const favQuery = useQuery({ queryKey: ['favorites'], queryFn: api.favorites, staleTime: 30_000 });
  const subQuery = useQuery({ queryKey: ['subscriptions'], queryFn: api.subscriptions, staleTime: 30_000 });

  const setCart = (cart: Cart) => qc.setQueryData(['cart'], cart);

  const addMutation = useMutation({
    mutationFn: (p: ProductCard) => api.cartAdd(p.id, 1),
    onSuccess: (cart, p) => {
      setCart(cart);
      haptic.medium();
      track('add_to_cart', { productId: p.id, props: { price: p.price } });
    },
    onError: (e: unknown) => {
      haptic.error();
      showToast(e instanceof ApiClientError ? e.message : 'Не удалось добавить товар');
    },
  });

  const qtyMutation = useMutation({
    mutationFn: ({ itemId, quantity }: { itemId: string; quantity: number }) =>
      api.cartSetQty(itemId, quantity),
    onSuccess: setCart,
    onError: () => showToast('Не удалось изменить количество'),
  });

  const removeMutation = useMutation({
    mutationFn: (itemId: string) => api.cartRemove(itemId),
    onSuccess: setCart,
    onError: () => showToast('Не удалось удалить позицию'),
  });

  const clearMutation = useMutation({
    mutationFn: () => api.cartClear(),
    onSuccess: setCart,
    onError: () => showToast('Не удалось очистить корзину'),
  });

  const favMutation = useMutation({
    mutationFn: async (p: ProductCard) => {
      const isFav = (favQuery.data ?? []).some((f) => f.id === p.id);
      if (isFav) await api.favoriteRemove(p.id);
      else await api.favoriteAdd(p.id);
      return !isFav;
    },
    onSuccess: (nowFav, p) => {
      haptic.light();
      track(nowFav ? 'favorite_add' : 'favorite_remove', { productId: p.id });
      void qc.invalidateQueries({ queryKey: ['favorites'] });
    },
    onError: () => showToast('Не удалось изменить избранное'),
  });

  const subMutation = useMutation({
    mutationFn: (p: ProductCard) => api.subscribeStock(p.id),
    onSuccess: (_r, p) => {
      haptic.success();
      showToast(`Напишем, когда «${p.name}» появится`);
      void qc.invalidateQueries({ queryKey: ['subscriptions'] });
    },
    onError: () => showToast('Не удалось оформить подписку'),
  });

  const cart = cartQuery.data;
  const favorites = favQuery.data ?? [];
  const subscribedIds = subQuery.data?.stock ?? [];

  const value = useMemo<ShopState>(() => ({
    cart,
    cartLoading: cartQuery.isLoading,
    cartCount: cart?.count ?? 0,
    cartTotal: cart?.itemsTotal ?? 0,
    inCart: (id) => Boolean(cart?.items.some((i) => i.product.id === id)),
    addToCart: async (p) => {
      if (isPriceOnRequest(p.price)) { showToast('Цена по запросу — напишите менеджеру'); return; }
      await addMutation.mutateAsync(p);
    },
    setQuantity: async (itemId, quantity) => { await qtyMutation.mutateAsync({ itemId, quantity }); },
    removeItem: async (itemId) => { await removeMutation.mutateAsync(itemId); },
    clearCart: async () => { await clearMutation.mutateAsync(); },
    busy: addMutation.isPending || qtyMutation.isPending || removeMutation.isPending || clearMutation.isPending,

    favorites,
    isFavorite: (id) => favorites.some((f) => f.id === id),
    toggleFavorite: async (p) => { await favMutation.mutateAsync(p); },

    subscribedIds,
    isSubscribed: (id) => subscribedIds.includes(id),
    subscribeStock: async (p) => { await subMutation.mutateAsync(p); },

    toast,
    showToast,
  }), [
    cart, cartQuery.isLoading, favorites, subscribedIds, toast, showToast,
    addMutation, qtyMutation, removeMutation, clearMutation, favMutation, subMutation,
  ]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useShop(): ShopState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useShop должен вызываться внутри ShopProvider');
  return ctx;
}

export function useNotificationPrefs() {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ['notif-prefs'], queryFn: api.notifications, staleTime: 60_000 });
  const mutation = useMutation({
    mutationFn: (patch: Partial<NotificationPreferences>) => api.setNotifications(patch),
    onMutate: async (patch) => {
      // Оптимистично: переключатель не должен «залипать» на время запроса
      await qc.cancelQueries({ queryKey: ['notif-prefs'] });
      const prev = qc.getQueryData<NotificationPreferences>(['notif-prefs']);
      if (prev) qc.setQueryData(['notif-prefs'], { ...prev, ...patch });
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(['notif-prefs'], ctx.prev);
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: ['notif-prefs'] }),
  });
  return { prefs: query.data, isLoading: query.isLoading, update: mutation.mutate };
}
